import * as React from 'react';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
type Options = { open: boolean; containerRef: React.RefObject<HTMLElement>; onClose: () => void; closeOnEscape?: boolean; initialFocusRef?: React.RefObject<HTMLElement> };

export function useOverlayFocus({ open, containerRef, onClose, closeOnEscape = true, initialFocusRef }: Options) {
  const closeRef = React.useRef(onClose);
  React.useEffect(() => { closeRef.current = onClose; }, [onClose]);
  React.useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const appRoot = document.getElementById('root');
    const priorOverflow = document.body.style.overflow;
    const priorPadding = document.body.style.paddingRight;
    const priorHidden = appRoot?.getAttribute('aria-hidden') ?? null;
    const priorInert = appRoot?.inert ?? false;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    if (appRoot) { appRoot.inert = true; appRoot.setAttribute('aria-hidden', 'true'); }
    const focus = () => {
      const container = containerRef.current;
      const target = initialFocusRef?.current ?? container?.querySelector<HTMLElement>(FOCUSABLE) ?? container;
      target?.focus();
    };
    const frame = window.requestAnimationFrame(focus);
    const onKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) return;
      if (event.key === 'Escape' && closeOnEscape) { event.preventDefault(); closeRef.current(); return; }
      if (event.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) { event.preventDefault(); container.focus(); return; }
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame); document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = priorOverflow; document.body.style.paddingRight = priorPadding;
      if (appRoot) { appRoot.inert = priorInert; if (priorHidden === null) appRoot.removeAttribute('aria-hidden'); else appRoot.setAttribute('aria-hidden', priorHidden); }
      trigger?.focus();
    };
  }, [closeOnEscape, initialFocusRef, open, containerRef]);
}
